import { GraphQLBilletsRequester, type MultipleContractDto } from '@cobliteam/api-lib';
import type { Billet } from '@cobliteam/api-lib/src/__generated__/graphql';
import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

const QUERY_KEY = 'BILLETS';

type GetBilletsParams = {
  companyId: string;
  options?: Partial<UseQueryOptions<Billet[]>>;
};

export const calculateOverdueDays = (dueDate: string) => {
  const getStartOfDay = (date: Date) => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };

  const today = getStartOfDay(new Date());
  const due = getStartOfDay(new Date(dueDate));

  const diffTime = today.getTime() - due.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

const requester = new GraphQLBilletsRequester();

const useBillets = (params: GetBilletsParams) => {
  return useQuery<Billet[]>({
    ...params.options,
    queryKey: [QUERY_KEY, params.companyId],
    queryFn: () => requester.getBillets(params.companyId),
    enabled: !!params.companyId && (params.options?.enabled ?? true),
  });
};

const useOverdueBillets = (multipleContracts?: MultipleContractDto) => {
  const companyIds = multipleContracts?.data?.map((contract) => contract.companyId).filter(Boolean);
  const hasContracts = !!companyIds && companyIds.length > 0;

  const { data, isLoading, isFetching, isError } = useQuery<Billet[]>({
    queryKey: [QUERY_KEY, 'byCompanies', companyIds],
    queryFn: () => requester.getBilletsByCompanies(companyIds!),
    enabled: hasContracts,
  });

  const overdueBillets = useMemo(
    () =>
      (data ?? []).filter((billet) => {
        if (!['03', '07'].includes(billet.status || '')) return false;
        if (!billet.dataVencimento) return false;
        return calculateOverdueDays(billet.dataVencimento) > 0;
      }),
    [data]
  );

  return { data: overdueBillets, isLoading, isFetching, isError };
};

export { useBillets, useOverdueBillets, QUERY_KEY };
export default useBillets;
